import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box, Card, CardContent, Container, Grid, Skeleton, IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ZicassoChat,
  BackNavigationAppBar,
  getFormattedDateLongMonthDDYYYY,
  OutlinedIcons,
  getBudgetPerPersonString,
  getTotalBudgetString,
  Logo as ZicassoLogo,
} from '@marageti/z4-lib';
import {
  Message,
  Conversation,
  GetParticipantsResponse,
  TripRequest,
} from '@marageti/z4-sdk/lib/travel';
import { PersonType } from '@marageti/z4-sdk/lib/people';
import MobileAlertDialog from '../../../../components/legacy-trip-request-dialog';

const formatDepartAndReturnDates = (dates: string[]) => (
  `${getFormattedDateLongMonthDDYYYY(dates[0]) || ''} - ${getFormattedDateLongMonthDDYYYY(dates[1]) || ''}`
);

type Props = {
  conversationTitle?: string,
  conversation: Conversation | undefined,
  handleViewClick: (message: Message) => void,
  isError: boolean,
  isLoading: boolean,
  isValidating: boolean,
  messages: Message[] | undefined,
  lastMessageSeenId: string | undefined,
  participants: GetParticipantsResponse | undefined,
  sendMessage: (message: string) => void,
  loadMoreMessages: () => void,
  hasMoreMessages: boolean,
  viewer: {
    id: string | undefined,
    type: PersonType,
  },
  tripRequest: TripRequest | undefined,
  isLegacyTR?: boolean,
  readOnly?: boolean,
};

const MessagesView = ({
  conversationTitle = '',
  conversation,
  handleViewClick,
  isError,
  isLoading,
  isValidating,
  messages,
  lastMessageSeenId,
  participants,
  sendMessage,
  loadMoreMessages,
  hasMoreMessages,
  viewer,
  tripRequest,
  isLegacyTR = false,
  readOnly = false,
}: Props) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));

  const [dialogOpen, setDialogOpen] = useState(false);

  const handleDialogOpen = () => {
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  // Use explicit path so we never go back to auth pages (e.g. after login redirect).
  const handleBack = useCallback(() => {
    const targetPath = tripRequest?.id ? `/trip-requests/${tripRequest.id}` : '/';
    navigate(targetPath, { replace: true });
  }, [navigate, tripRequest?.id]);

  const {
    OutlinedInformationCircleIcon,
  } = OutlinedIcons;

  return (
    <Container
      maxWidth="xl"
      sx={{
        ...(isMobile ? {
          position: 'fixed',
          inset: 0,
          backgroundColor: 'primary.contrastText',
          zIndex: 1200,
          padding: '0 !important',
        } : {
          display: 'flex',
          flex: 1,
          pt: 3.75,
        }),
      }}
    >
      {/* Mobile Header */}
      {isMobile && (
      <>
        {isLegacyTR && <MobileAlertDialog open={dialogOpen} onClose={handleDialogClose} />}
        <BackNavigationAppBar
          avatar={{
            src: ZicassoLogo,
          }}
          primary={conversationTitle || <Skeleton width={150} />}
          onClick={handleBack}
          endAdornment={isLegacyTR ? (
            <IconButton onClick={handleDialogOpen}>
              <OutlinedInformationCircleIcon color="primary" />
            </IconButton>
          ) : null}
        />
      </>
      )}

      <Grid
        container
        spacing={isMobile ? 3 : 4}
        sx={{
          ...(!isMobile && {
            pl: 0,
          }),
        }}
      >
        {/* Sticky Sidebar */}
        {!isMobile && (
          <Grid
            item
            desktop={4}
            sx={{
              display: 'block',
              borderRight: '1px solid #C2C2C2',
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: () => {
                  const toolbarHeight = theme.mixins.toolbar.minHeight;
                  return `calc(${toolbarHeight}px + ${theme.spacing(6)})`;
                },
                pr: 3,
                pb: 3,
                height: 'calc(100vh - 186px)',
                overflow: 'auto',
              }}
            >
              <Stack spacing={4}>
                {isLegacyTR && (
                  <Alert
                    severity="warning"
                    sx={{
                      p: 3,
                      backgroundColor: 'warning.light',
                    }}
                    variant="outlined"
                  >
                    <AlertTitle>Prior Communication</AlertTitle>
                    Zicasso has moved to a new messaging platform. Previous email
                    communications are not included in this chat.
                  </Alert>
                )}
                <Box>
                  {tripRequest && !tripRequest.visibilityFlags.hideDetails && (
                  <Card>
                    <CardContent>
                      <Stack spacing={0.25}>
                        <Typography variant="h4" sx={{ pb: 1.25 }}>Original Request</Typography>
                        <Typography variant="body1" color="text.secondary">
                          {formatDepartAndReturnDates([
                            tripRequest.departureDate,
                            tripRequest.returnDate,
                          ])}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {`Budget Per Person: ${getBudgetPerPersonString(
                            tripRequest.totalBudget,
                            tripRequest.numberOfAdults,
                            tripRequest.numberOfChildren,
                          )}`}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {`Total Budget: ${getTotalBudgetString(
                            tripRequest.totalBudget,
                          )}`}
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {`${tripRequest.numberOfAdults} Adults${
                            tripRequest.numberOfChildren
                              ? `, ${tripRequest.numberOfChildren} Children`
                              : ''
                          }`}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                  )}
                </Box>
              </Stack>
            </Box>
          </Grid>
        )}

        {/* Main Content */}
        <Grid
          item
          desktop={isMobile ? 12 : 8}
          sx={{
            ...(isMobile ? {
              display: 'flex',
              flex: 1,
              p: 0,
              minHeight: 'calc(100vh - 72px)',
            } : { pl: 3 }),
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              width: '100%',
              overflow: 'hidden',
              maxWidth: '100vw',
              height: isMobile ? 'calc(100dvh - 72px)' : 'calc(100vh - 198px)',
              ...(isMobile && { px: 3 }),
            }}
          >
            <ZicassoChat
              hideToolbarOnMobile
              isError={isError}
              isLoading={isLoading}
              isValidating={isValidating}
              conversation={conversation}
              messages={messages}
              lastMessageSeenId={lastMessageSeenId}
              loadPreviousPages={hasMoreMessages}
              onLoadPreviousPage={loadMoreMessages}
              onSend={sendMessage}
              onViewClick={handleViewClick}
              participants={participants}
              viewer={viewer}
              readOnly={readOnly}
              messagingAlert={(
                <>
                  Always communicate through this messaging platform to ensure you’re covered by our terms of service, privacy policy and other safeguards.
                </>
              )}
            />
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

export default MessagesView;
